/**
 * Zod validation schemas for the Whakahaere config UI (ADR-126
 * Decisions 3 & 4). Mirrors the backend `clean()` constraints so the form
 * surfaces the same errors client-side before the round-trip:
 *   - operating hours / custom-hours exceptions: open_time < close_time
 *   - CUSTOM_HOURS exceptions require both open_time and close_time
 *   - scheduled events: duration_minutes > 0
 *   - org XOR site attachment (exactly one of district / school)
 */

import { z } from 'zod';

/** `HH:MM` (24h). The backend stores `TimeField`; the form edits minute-resolution. */
const timeString = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Time must be HH:MM format');

/** `YYYY-MM-DD`. */
const dateString = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD format');

const weekday = z
  .number()
  .int()
  .min(0, 'Invalid weekday')
  .max(6, 'Invalid weekday');

// ---------------------------------------------------------------------------
// Calendar
// ---------------------------------------------------------------------------

export const operatingCalendarFormSchema = z
  .object({
    name: z.string().max(120, 'Name is too long').optional(),
    district: z.number().int().positive().nullable().optional(),
    school: z.number().int().positive().nullable().optional(),
    term: z.number().int().positive().nullable().optional(),
    always_operating: z.boolean(),
  })
  .refine(
    (data) => Boolean(data.district) !== Boolean(data.school),
    {
      message:
        'Attach the calendar to exactly one of an organization or a site.',
      path: ['school'],
    },
  );

export type OperatingCalendarFormValues = z.infer<
  typeof operatingCalendarFormSchema
>;

// ---------------------------------------------------------------------------
// Operating hours
// ---------------------------------------------------------------------------

export const operatingHoursFormSchema = z
  .object({
    weekday,
    open_time: timeString,
    close_time: timeString,
  })
  .refine((data) => data.open_time < data.close_time, {
    message: 'Open time must be earlier than close time.',
    path: ['close_time'],
  });

export type OperatingHoursFormValues = z.infer<typeof operatingHoursFormSchema>;

// ---------------------------------------------------------------------------
// Operating exception (dated override)
// ---------------------------------------------------------------------------

export const operatingExceptionFormSchema = z
  .object({
    date: dateString,
    kind: z.enum(['closed', 'open', 'custom_hours']),
    open_time: timeString.optional().or(z.literal('')),
    close_time: timeString.optional().or(z.literal('')),
    reason: z.string().max(200, 'Reason is too long').optional(),
  })
  .superRefine((data, ctx) => {
    if (data.kind !== 'custom_hours') return;
    if (!data.open_time) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Custom hours require an open time.',
        path: ['open_time'],
      });
    }
    if (!data.close_time) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Custom hours require a close time.',
        path: ['close_time'],
      });
    }
    if (data.open_time && data.close_time && data.open_time >= data.close_time) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Open time must be earlier than close time.',
        path: ['close_time'],
      });
    }
  });

export type OperatingExceptionFormValues = z.infer<
  typeof operatingExceptionFormSchema
>;

// ---------------------------------------------------------------------------
// Scheduled site event
// ---------------------------------------------------------------------------

export const scheduledSiteEventFormSchema = z.object({
  event_type: z.enum(['drill', 'training', 'field_exercise', 'other']),
  name: z.string().max(255, 'Name is too long').optional(),
  description: z.string().optional(),
  /** `datetime-local` value — ISO-ish `YYYY-MM-DDTHH:MM`. */
  start: z.string().min(1, 'Start is required'),
  duration_minutes: z
    .number()
    .int('Duration must be a whole number of minutes')
    .positive('Duration must be greater than zero'),
  recurrence_rule: z.string().optional(),
});

export type ScheduledSiteEventFormValues = z.infer<
  typeof scheduledSiteEventFormSchema
>;

// ---------------------------------------------------------------------------
// Routing policy
// ---------------------------------------------------------------------------

export const routingPolicyFormSchema = z
  .object({
    name: z.string().max(120, 'Name is too long').optional(),
    district: z.number().int().positive().nullable().optional(),
    school: z.number().int().positive().nullable().optional(),
    alert_type: z.number().int().positive().nullable().optional(),
    when_state: z.enum(['not_operating', 'operating', 'any']),
    when_event: z.enum([
      'any',
      'no_event',
      'any_event',
      'drill',
      'training',
      'field_exercise',
      'other',
    ]),
    adjustment: z.enum(['no_adjustment', 'restrict_to_on_duty', 'suppress_all']),
    priority: z
      .number()
      .int('Priority must be a whole number')
      .min(0, 'Priority cannot be negative'),
    is_active: z.boolean(),
  })
  .refine(
    (data) => Boolean(data.district) !== Boolean(data.school),
    {
      message:
        'Attach the policy to exactly one of an organization or a site.',
      path: ['school'],
    },
  );

export type RoutingPolicyFormValues = z.infer<typeof routingPolicyFormSchema>;

// ---------------------------------------------------------------------------
// Terminology overlay
// ---------------------------------------------------------------------------

export const operatingTerminologyFormSchema = z.object({
  operating_label: z
    .string()
    .min(1, 'An operating label is required')
    .max(60, 'Label is too long'),
  not_operating_label: z
    .string()
    .min(1, 'A not-operating label is required')
    .max(60, 'Label is too long'),
  event_label: z.string().max(60, 'Label is too long').optional(),
});

export type OperatingTerminologyFormValues = z.infer<
  typeof operatingTerminologyFormSchema
>;
